import { Link } from 'react-router-dom';
import { Shield, Vote, TreePine, MapPin, Users, Clock, FileText, Check, ArrowRight, Star, Crown, Building } from 'lucide-react';
import { Button } from '../components/ui/button';

const SEAT_IMG = "https://customer-assets.emergentagent.com/job_protect-florida-now/artifacts/t5c23o6q_BDDA1D3F-8DB3-4E73-9795-D4ACFC0440FD.PNG";

const votingRights = [
  { text: 'Which land parcels Safe Florida should pursue for acquisition and preservation — stopping developers before they break ground' },
  { text: 'Which legal cases the Legal Defense Fund should finance' },
  { text: 'Which municipalities and counties become Priority Zones for concentrated advocacy' },
  { text: 'Which elected officials receive Safe Florida endorsement or opposition based on their record' },
  { text: 'Community resolutions and public positions Safe Florida takes on development proposals' },
];

const howItWorks = [
  { step: 1, title: 'Identify', desc: 'Safe Florida identifies a land parcel or development threat.' },
  { step: 2, title: 'Brief', desc: 'Members receive a full briefing with maps, developer plans, and environmental impact.' },
  { step: 3, title: 'Vote', desc: 'Members vote within 72 hours.' },
  { step: 4, title: 'Act', desc: 'Safe Florida acts on the majority vote — acquiring land, filing legal challenge, or mobilizing community opposition.' },
  { step: 5, title: 'Update', desc: 'Members receive updates on outcomes and next steps.' },
];

const tierAccess = [
  { tier: 'Founding Member', price: '$250/month', access: 'Full voting rights on all decisions, land acquisitions, legal cases, and endorsements', icon: Crown, highlight: true },
  { tier: 'Organizational Partner', price: '$500/month', access: 'Full voting rights plus multi-seat access for your organization (5 votes)', icon: Building, highlight: true },
  { tier: 'Advocate', price: '$99/month', access: 'Advisory vote and full briefing access on all decisions. Voice heard in deliberations.', icon: Shield, highlight: false },
  { tier: 'Community Member', price: '$25/month', access: 'Briefing access and community forum participation. Observer status on votes.', icon: Users, highlight: false },
  { tier: 'Supporter', price: '$10/month', access: 'Updates on outcomes and community impact reports.', icon: Star, highlight: false },
];

export default function SeatAtTable() {
  return (
    <div className="pt-16" data-testid="seat-at-table-page">
      {/* Hero Banner */}
      <section className="pt-8 pb-6 bg-[#FAFAFA]">
        <div className="max-w-[1200px] mx-auto px-4">
          <img
            src={SEAT_IMG}
            alt="Safe Florida - A Seat at the Table. A Voice for Our Land, Our Wildlife, and Our Waters."
            className="w-full h-auto block rounded-xl"
            style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}
            data-testid="seat-banner-image"
          />
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 bg-white" data-testid="seat-title-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-4">Seat at the Table</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-navy mb-4 leading-tight">
            A Seat at the Table — Real Representation. Real Impact.
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 font-body italic">
            Not on the sidelines. At the table where decisions are made.
          </p>
        </div>
      </section>

      {/* SECTION 1 — WHAT THIS MEANS */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="seat-what-this-means">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border-l-4 border-gold pl-6">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-6">What This Means</h2>
            <p className="text-base text-gray-700 font-body leading-relaxed">
              When you become a Seat at the Table member, you are not just supporting Safe Florida — you are an active participant in the decisions that shape Florida's land, communities, and future. You get a direct voice and vote on critical issues before they are decided.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — YOUR VOTING RIGHTS */}
      <section className="py-20 bg-white" data-testid="seat-voting-rights">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Vote className="w-10 h-10 text-gold mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">Your Voting Rights</h2>
            <p className="text-base text-gray-600 font-body">As a Seat at the Table member, you vote on:</p>
          </div>
          <div className="space-y-4">
            {votingRights.map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#FAFAFA] border border-gray-200 rounded-lg p-5 card-hover" data-testid={`voting-right-${i}`}>
                <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-heading font-bold text-sm">{i + 1}</span>
                </div>
                <p className="text-base text-gray-700 font-body leading-relaxed pt-1">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — LAND ACQUISITION POWER */}
      <section className="py-20 bg-navy" data-testid="seat-land-acquisition">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <TreePine className="w-10 h-10 text-gold mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-6">Land Acquisition Power</h2>
          </div>
          <div className="bg-white/10 border border-white/20 rounded-lg p-8">
            <p className="text-base text-white/90 font-body leading-relaxed mb-6">
              Once land is lost, it is gone forever. Safe Florida members identify, vote on, and fund strategic land acquisitions across Florida.
            </p>
            <p className="text-base text-white/90 font-body leading-relaxed mb-6">
              When a developer targets a wetland, a conservation area, or community green space — our members vote to act. We pool membership resources to acquire, preserve, or legally challenge development on land that belongs to Florida's future — not to outside developers.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { label: 'Wetlands', desc: 'Critical habitat preservation' },
                { label: 'Conservation Areas', desc: 'Wildlife corridor protection' },
                { label: 'Green Spaces', desc: 'Community land defense' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-white/10 border border-white/20 rounded-lg p-4 text-center">
                  <p className="font-heading font-bold text-gold text-sm">{label}</p>
                  <p className="text-xs text-white/70 font-body mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — HOW IT WORKS */}
      <section className="py-20 bg-white" data-testid="seat-how-it-works">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Clock className="w-10 h-10 text-navy mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">How It Works</h2>
            <p className="text-base text-gray-600 font-body">From identification to action — a transparent, member-driven process.</p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gold/30 -translate-x-1/2" />
            <div className="space-y-8">
              {howItWorks.map(({ step, title, desc }, i) => (
                <div key={step} className={`md:flex items-center gap-8 ${i % 2 === 0 ? '' : 'md:flex-row-reverse'}`} data-testid={`step-${step}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`bg-[#FAFAFA] border border-gray-200 rounded-lg p-6 inline-block max-w-md ${i % 2 === 0 ? 'md:ml-auto' : ''}`}>
                      <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-1">Step {step}</p>
                      <h3 className="font-heading font-bold text-navy text-lg mb-2">{title}</h3>
                      <p className="text-sm text-gray-700 font-body leading-relaxed">{desc}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-12 h-12 bg-navy rounded-full items-center justify-center flex-shrink-0 z-10 border-4 border-white">
                    <span className="text-gold font-heading font-bold">{step}</span>
                  </div>
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHO SITS AT THE TABLE */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="seat-who-sits">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Users className="w-10 h-10 text-forest mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy mb-3">Who Sits at the Table</h2>
            <p className="text-base text-gray-600 font-body max-w-2xl mx-auto">
              Every member level has a role. Founding Members and Organizational Partners have full voting power.
            </p>
          </div>
          <div className="space-y-4">
            {tierAccess.map(({ tier, price, access, icon: Icon, highlight }) => (
              <div key={tier} className={`flex items-start gap-4 rounded-lg p-5 border ${highlight ? 'border-gold bg-gold/5' : 'border-gray-200 bg-white'}`} data-testid={`tier-access-${tier.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-navy' : 'bg-navy/5'}`}>
                  <Icon className={`w-6 h-6 ${highlight ? 'text-gold' : 'text-navy'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-heading font-bold text-navy text-base">{tier}</h3>
                    <span className="text-sm font-body font-bold text-gold">{price}</span>
                    {highlight && <span className="text-xs bg-gold text-navy px-2 py-0.5 rounded font-body font-bold">FULL VOTING</span>}
                  </div>
                  <p className="text-sm text-gray-700 font-body leading-relaxed">{access}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Take Your Seat</h2>
          <p className="text-lg text-white/80 font-body mb-4">
            This is not a donation. This is direct democratic action for Florida's future.
          </p>
          <p className="font-signature text-gold text-3xl mb-8">Real Representation. Real Impact. — F.C. Clark</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/programs/membership" data-testid="seat-cta-founding">
              <Button size="lg" className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-8 rounded-md">
                FOUNDING MEMBER — $250/mo
              </Button>
            </Link>
            <Link to="/programs/membership" data-testid="seat-cta-all-tiers">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-body font-bold px-8 rounded-md">
                VIEW ALL TIERS
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
